/// <amd-module name="VSS/Platform/Util/Storage" />

/**
 * Best-effort attempt to retrieve a string value from local storage
 *
 * @param key Storage key
 * @param defaultValue Optional value to return if the key was not present or there was an error retrieving it
 */
export function tryGetLocalStorageValue(key: string, defaultValue: string | null = null): string | null {
    let result: string | null = null;
    try {
        result = localStorage.getItem(key);
    } catch {}

    return result === null ? defaultValue : result;
}

/**
 * Best-effort attempt to retrieve an object from local storage (using JSON.parse)
 *
 * @param key Storage key
 * @param defaultValue Optional value to return if the key was not present or there was an error retrieving it
 */
export function tryGetLocalStorageObject<T>(key: string, defaultValue?: T): T | undefined {
    const stringValue = tryGetLocalStorageValue(key);
    if (!stringValue) {
        return defaultValue;
    } else {
        try {
            return JSON.parse(stringValue);
        } catch {
            return defaultValue;
        }
    }
}

/**
 * Best-effort attempt to write a string value to local storage
 *
 * @param key Storage key
 * @param value Storage value
 */
export function trySetLocalStorageValue(key: string, value: string | undefined): boolean {
    try {
        if (value === null || value === undefined) {
            localStorage.removeItem(key);
        } else {
            localStorage.setItem(key, value);
        }
        return true;
    } catch {
        return false;
    }
}

/**
 * Best-effort attempt to write an object to local storage (stored using JSON.stringify)
 *
 * @param key Storage key
 * @param value Storage value
 */
export function trySetLocalStorageObject<T>(key: string, value: T): boolean {
    const valueToWrite = value ? JSON.stringify(value) : undefined;
    return trySetLocalStorageValue(key, valueToWrite);
}

/**
 * Best-effort attempt to retrieve a string value from session storage
 *
 * @param key Storage key
 * @param defaultValue Optional value to return if the key was not present or there was an error retrieving it
 */
export function tryGetSessionStorageValue(key: string, defaultValue: string | null = null): string | null {
    let result: string | null = null;
    try {
        result = sessionStorage.getItem(key);
    } catch { }

    return result === null ? defaultValue : result;
}

/**
 * Best-effort attempt to retrieve an object from session storage (using JSON.parse)
 *
 * @param key Storage key
 * @param defaultValue Optional value to return if the key was not present or there was an error retrieving it
 */
export function tryGetSessionStorageObject<T>(key: string, defaultValue?: T): T | undefined {
    const stringValue = tryGetSessionStorageValue(key);
    if (!stringValue) {
        return defaultValue;
    } else {
        try {
            return JSON.parse(stringValue);
        } catch {
            return defaultValue;
        }
    }
}

/**
 * Best-effort attempt to write a string value to session storage
 *
 * @param key Storage key
 * @param value Storage value
 */
export function trySetSessionStorageValue(key: string, value: string | undefined): boolean {
    try {
        if (value === null || value === undefined) {
            sessionStorage.removeItem(key);
        } else {
            sessionStorage.setItem(key, value);
        }
        return true;
    } catch {
        return false;
    }
}

/**
 * Best-effort attempt to write an object to session storage (stored using JSON.stringify)
 *
 * @param key Storage key
 * @param value Storage value
 */
export function trySetSessionStorageObject<T>(key: string, value: T): boolean {
    const valueToWrite = value ? JSON.stringify(value) : undefined;
    return trySetSessionStorageValue(key, valueToWrite);
}